/**
 * useConfirmDialog - Hook para uso imperativo do ConfirmDialog
 * 
 * Permite usar o diálogo de confirmação de forma similar ao window.confirm(),
 * mas com interface moderna e estilizada.
 * 
 * @example
 * const { confirmDialog, ConfirmDialogComponent } = useConfirmDialog();
 * 
 * const handleDelete = async () => {
 *   const confirmed = await confirmDialog({
 *     title: 'Excluir item?',
 *     variant: 'danger',
 *   });
 *   if (confirmed) {
 *     // executa exclusão
 *   }
 * };
 * 
 * return (
 *   <>
 *     <Button onClick={handleDelete}>Excluir</Button>
 *     {ConfirmDialogComponent}
 *   </>
 * );
 */

import * as React from 'react';
import { useState, useCallback, useRef } from 'react';
import { ConfirmDialog, ConfirmDialogVariant } from '@/components/ui/confirm-dialog';

// Opções para o diálogo de confirmação
export interface ConfirmDialogOptions {
  /** Título principal do diálogo */
  title: string;
  /** Descrição opcional abaixo do título */
  description?: string;
  /** Mensagem de aviso destacada (opcional) */
  warningMessage?: string;
  /** Variante visual: danger (vermelho), warning (amarelo), info (azul) */
  variant?: ConfirmDialogVariant;
  /** Ícone customizado */
  icon?: React.ReactNode;
  /** Texto do botão de confirmação */
  confirmText?: string;
  /** Texto do botão de cancelamento */
  cancelText?: string;
  /** Exibir ícone no topo */
  showIcon?: boolean;
}

// Retorno do hook
interface UseConfirmDialogReturn {
  /** Função para abrir o diálogo e aguardar resposta */
  confirmDialog: (options: ConfirmDialogOptions) => Promise<boolean>;
  /** Componente JSX para renderizar no componente pai */
  ConfirmDialogComponent: React.ReactNode;
}

export function useConfirmDialog(): UseConfirmDialogReturn {
  // Estado do diálogo
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmDialogOptions | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Referência para resolver a promise
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  // Função para abrir o diálogo e retornar uma promise
  const confirmDialog = useCallback(
    (dialogOptions: ConfirmDialogOptions): Promise<boolean> => {
      return new Promise((resolve) => {
        resolveRef.current = resolve;
        setOptions(dialogOptions);
        setOpen(true);
      });
    },
    []
  );

  // Handler para confirmação
  const handleConfirm = useCallback(() => {
    resolveRef.current?.(true);
    resolveRef.current = null;
    setOpen(false);
    setOptions(null);
  }, []);

  // Handler para cancelamento
  const handleCancel = useCallback(() => {
    resolveRef.current?.(false);
    resolveRef.current = null;
    setOpen(false);
    setOptions(null);
  }, []);

  // Handler para mudança de estado (fechamento pelo overlay, ESC, etc.)
  const handleOpenChange = useCallback((newOpen: boolean) => {
    if (!newOpen) {
      // Se está fechando, considera como cancelamento
      resolveRef.current?.(false);
      resolveRef.current = null;
      setOptions(null);
    }
    setOpen(newOpen);
  }, []);

  // Componente JSX para renderizar
  const ConfirmDialogComponent = options ? (
    <ConfirmDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={options.title}
      description={options.description}
      warningMessage={options.warningMessage}
      variant={options.variant}
      icon={options.icon}
      confirmText={options.confirmText}
      cancelText={options.cancelText}
      showIcon={options.showIcon}
      isLoading={isLoading}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  ) : null;

  return {
    confirmDialog,
    ConfirmDialogComponent,
  };
}

export default useConfirmDialog;
